import { IFinancialAgreement } from '@nkr/store/match';
import { Page, Text, View, Document, Font, Image, Link } from '@react-pdf/renderer';
import MontserratRegular from 'components/assets/fonts/Montserrat-Regular.ttf';
import MontserratBold from 'components/assets/fonts/Montserrat-Bold.ttf';
import MontserratItalic from 'components/assets/fonts/Montserrat-Italic.ttf'
import { styles } from './financial-agreement-style';

Font.register({
    family: 'Montserrat',
    fonts: [
        { src: MontserratRegular }, // Regular
        { src: MontserratBold, fontWeight: 'bold' }, // Bold
        { src: MontserratItalic, fontStyle: 'italic' }, // Italic
        // Add other font styles as needed (e.g., italic)
    ],
});

type TProps = {
    agreements: IFinancialAgreement[]
}

const FinancialAgreementDoc = ({ agreements }: TProps) => {
    const pages = agreements.map((agreement, i) => {
        return <>
            <Page key={`${i}-page-1`} size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        <Image src={'./images/logo2.png'} style={styles.logo} />
                    </View>
                    <View style={styles.title}>
                        <Text>Financial Agreement Form</Text>
                    </View>
                </View>
                <View style={styles.swapRow}>
                    <View style={styles.swapNumberBox}>
                        <View style={styles.swapNumberLabel}>
                            <Text style={styles.swapNumberLabelText}>NKR Swap #:</Text>
                        </View>
                        <View style={styles.swapNumberInput} >
                            <Text style={styles.swapNumberText}>{agreement?.swapLabel}</Text>
                        </View>
                    </View>
                </View>

                {/* Billing Information Section */}
                <Text style={styles.sectionTitle}>Billing Information</Text>
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableRow}>
                        <View style={[styles.labelCell,]} />
                        <View style={[styles.tableHeaderCell, styles.donorHeader]}>
                            <Text>Donor Center:</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, styles.recipientHeader]}>
                            <Text>Recipient Center:</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Hospital Name to Bill:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorCenter}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientCenter}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Billing Contact Name:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorBillingContactName}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientBillingContactName}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Billing Contact Phone:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorBillingContactPhoneNumber}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientBillingContactPhoneNumber}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Street Address:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorBillingAddress}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientBillingAddress}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>City, State & Zip:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorBillingCsz}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientBillingCsz}</Text>
                        </View>
                    </View>

                </View>

                {/* Center Primary Contact Information Section */}
                <Text style={styles.sectionTitle}>Center Primary Contact Information</Text>
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableRow}>
                        <View style={[styles.labelCell,]} />
                        <View style={[styles.tableHeaderCell, styles.donorHeader]}>
                            <Text>Donor Center:</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, styles.recipientHeader]}>
                            <Text>Recipient Center:</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Name:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactName}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactName}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Phone:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactPhone}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactPhone}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Email:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactEmail}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactEmail}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Fax:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactFax}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactFax}</Text>
                        </View>
                    </View>

                </View>

                {/* Center Secondary Contact Information Section */}
                <Text style={styles.sectionTitle}>Center Secondary Contact Information</Text>
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableRow}>
                        <View style={[styles.labelCell,]} />
                        <View style={[styles.tableHeaderCell, styles.donorHeader]}>
                            <Text>Donor Center:</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, styles.recipientHeader]}>
                            <Text>Recipient Center:</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Name:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactBackupName}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactBackupName}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Phone:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactBackupPhone}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactBackupPhone}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Email:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactBackupEmail}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactBackupEmail}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Fax:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorFinancialContactBackupFax}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientFinancialContactBackupFax}</Text>
                        </View>
                    </View>

                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>Page {1 + (i * agreements.length)} of {agreements.length * 2}</Text>
                    <Text>Revised 10/31/2025</Text>
                </View>
            </Page>

            <Page key={`${i}-page-2`} size="LETTER" style={styles.page}>
                <View style={styles.header}>
                    <View style={styles.logoContainer}>
                        <Image src={'./images/logo2.png'} style={styles.logo} />
                    </View>
                    <View style={styles.title}>
                        <Text>Financial Agreement Form</Text>
                    </View>
                </View>

                {/* Donor/Recipient Information Section */}
                <Text style={styles.sectionTitle}>Donor/Recipient Information</Text>
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableRow}>
                        <View style={[styles.labelCell,]} />
                        <View style={[styles.tableHeaderCell, styles.donorHeader]}>
                            <Text>Donor:</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, styles.recipientHeader]}>
                            <Text>Recipient:</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Alias:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorAlias}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientAlias}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Name:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorName}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientName}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Street Address:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorAddress}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientAddress}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>City, State & Zip:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorCsz}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientCsz}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Date of Birth:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.donorDob}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.recipientDob}</Text>
                        </View>
                    </View>

                </View>

                {/* Recipient Insurance Section */}
                <Text style={styles.sectionTitle}>Recipient Insurance</Text>
                <View style={styles.table}>
                    {/* Header Row */}
                    <View style={styles.tableRow}>
                        <View style={[styles.labelCell,]} />
                        <View style={[styles.tableHeaderCell, styles.donorHeader]}>
                            <Text>Primary Insurance:</Text>
                        </View>
                        <View style={[styles.tableHeaderCell, styles.recipientHeader]}>
                            <Text>Secondary Insurance:</Text>
                        </View>
                    </View>

                    {/* Data Rows */}
                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Insurance:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.insurance}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.insurance2}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Medicare Number:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.medicare}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>{agreement?.medicare2}</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Global Case Rate:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.globalCaseRate}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Professional Case Rate:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.proCaseRate}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Policy Number:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.policyNumber}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Subscriber:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.subscriber}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Group ID:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.groupId}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Effective Date:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.insuranceLastUpdated}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Claim Address:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.claimsAddress}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                    <View style={styles.tableRow}>
                        <View style={styles.labelCell}>
                            <Text>Case Manager Name:</Text>
                        </View>
                        <View style={styles.inputCell}>
                            <Text>{agreement?.caseManagerName}</Text>
                        </View>
                        <View style={styles.inputCellBorder}>
                            <Text>--</Text>
                        </View>
                    </View>

                </View>

                {/* Billing Specifics Section */}
                <View style={styles.billingSpecificsSection}>
                    <Text style={styles.billingSpecificsTitle}>Billing Specifics</Text>

                    {/* Center to Center Billing paragraph */}
                    <View style={styles.billingParagraph}>
                        <Text style={styles.billingSpecificsSubtitle}>
                            Center to Center Billing (CCB):
                        </Text>
                        <Text style={styles.billingSpecificsText}>
                            The NKR shall invoice and reimburse MCs for donor evaluations and donor nephrectomies (facility fees) according to the Center to Center Reimbursement Section of the NKR Terms and Conditions (
                            <Link src="https://www.kidneyregistry.com/membership-terms/" style={styles.billingSpecificsLink}>
                                kidneyregistry.com/membership-terms
                            </Link>).
                        </Text>
                    </View>

                    {/* Donor Physician Fees paragraph */}
                    <View style={styles.billingParagraph}>
                        <Text style={styles.billingSpecificsSubtitle}>
                            Donor Physician Fees / Professional Fees:
                        </Text>
                        <Text style={styles.billingSpecificsText}>
                            MC shall bill these services based on the recipient insurance according to the NKR Terms and Conditions <br />(
                            <Link
                                src="https://www.kidneyregistry.com/membership-terms/"
                                style={styles.billingSpecificsLink}
                            >
                                kidneyregistry.com/membership-terms
                            </Link>).
                        </Text>
                    </View>
                </View>

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>Page {2 + (i * agreements.length)} of {agreements.length * 2}</Text>
                    <Text>Revised 10/31/2025</Text>
                </View>
            </Page>
        </>
    });

    return (
        <Document>
            {pages}
        </Document>
    )
};

export default FinancialAgreementDoc;